import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiTrash2, FiSend, FiUser, FiClock, FiMessageSquare } from 'react-icons/fi';
import { communityAPI } from '../../service/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { confirmAction, showError, showSuccess } from '../../service/swal';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function CommunityPostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useSocket();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);

  const loadPost = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const res = await communityAPI.getById(id);
      setPost(res.data.data);
    } catch {
      showError('Erreur', 'Publication introuvable');
      navigate('/communautes');
    } finally {
      if (!skipLoading) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadPost(); }, [loadPost]);
  useAutoRefresh(() => loadPost(true), 30000);

  useEffect(() => {
    if (!socket) return;
    const handleComment = () => {
      loadPost();
    };
    socket.on('new_comment', handleComment);
    return () => socket.off('new_comment', handleComment);
  }, [socket, loadPost]);

  const handleDelete = async () => {
    const confirmed = await confirmAction(
      'Supprimer la publication',
      'Cette action est irréversible.'
    );
    if (!confirmed) return;

    try {
      await communityAPI.delete(id);
      showSuccess('Publication supprimée');
      navigate('/communautes');
    } catch (err) {
      showError('Erreur', 'Impossible de supprimer la publication');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSending(true);
    try {
      await communityAPI.addComment(id, { contenu: commentText });
      setCommentText('');
      loadPost();
    } catch (err) {
      showError('Erreur', 'Impossible d\'ajouter le commentaire');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    const confirmed = await confirmAction(
      'Supprimer le commentaire',
      'Cette action est irréversible.'
    );
    if (!confirmed) return;

    try {
      await communityAPI.deleteComment(id, commentId);
      showSuccess('Commentaire supprimé');
      loadPost();
    } catch (err) {
      showError('Erreur', 'Impossible de supprimer le commentaire');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="page-container py-20 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent mx-auto" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent', borderRadius: '50%' }} />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="page-container py-8">
      <Link
        to="/communautes"
        className="inline-flex items-center gap-2 text-sm font-medium mb-6"
        style={{ color: '#88929B' }}
      >
        <FiArrowLeft className="w-4 h-4" />
        Retour à la communauté
      </Link>

      <div className="card-root p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-serif text-2xl md:text-3xl font-bold" style={{ color: '#0A192F' }}>
            {post.titre}
          </h1>
          {user && (user.id === post.userId || user.role === 'admin') && (
            <button
              onClick={handleDelete}
              className="flex-shrink-0 p-2 transition-colors"
              style={{ color: '#88929B' }}
              onMouseEnter={(e) => e.target.style.color = '#FF6B6B'}
              onMouseLeave={(e) => e.target.style.color = '#88929B'}
            >
              <FiTrash2 className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="mt-4 flex items-center gap-4 text-sm" style={{ color: '#88929B' }}>
          <span className="flex items-center gap-1.5">
            <FiUser className="w-4 h-4" />
            {post.auteurNom}
          </span>
          <span className="flex items-center gap-1.5">
            <FiClock className="w-4 h-4" />
            {formatDate(post.dateCreation)}
          </span>
          <span className="flex items-center gap-1.5">
            <FiMessageSquare className="w-4 h-4" />
            {post.comments?.length || 0} commentaire{(post.comments?.length || 0) > 1 ? 's' : ''}
          </span>
        </div>

        {post.tags && (
          <div className="mt-3 flex gap-2">
            {post.tags.split(',').map((tag) => (
              <span
                key={tag}
                className="text-xs font-medium px-2.5 py-1"
                style={{ backgroundColor: '#F4F4F0', color: '#C59B6C' }}
              >
                #{tag.trim()}
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 text-base leading-relaxed whitespace-pre-wrap" style={{ color: '#3A4045' }}>
          {post.contenu}
        </div>
      </div>

      <div className="card-root p-6 md:p-8">
        <h2 className="font-serif text-xl font-bold mb-6" style={{ color: '#0A192F' }}>
          Commentaires ({post.comments?.length || 0})
        </h2>

        {user ? (
          <form onSubmit={handleAddComment} className="mb-8">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Écrire un commentaire..."
              rows={3}
              className="w-full p-3 text-sm border"
              style={{ borderColor: '#E2E2E0', color: '#0A192F', borderRadius: 0, resize: 'vertical' }}
            />
            <button
              type="submit"
              disabled={sending || !commentText.trim()}
              className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold tracking-wider uppercase"
              style={{
                backgroundColor: commentText.trim() ? '#C59B6C' : '#E2E2E0',
                color: commentText.trim() ? '#0A192F' : '#88929B',
                borderRadius: 0,
                cursor: commentText.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              <FiSend className="w-4 h-4" />
              {sending ? 'Envoi...' : 'Commenter'}
            </button>
          </form>
        ) : (
          <div className="mb-8 p-4 text-sm text-center" style={{ backgroundColor: '#F4F4F0', color: '#7A828A' }}>
            <Link to="/login" className="font-medium underline" style={{ color: '#C59B6C' }}>
              Connectez-vous
            </Link>{' '}
            pour laisser un commentaire.
          </div>
        )}

        <div className="space-y-4">
          {(!post.comments || post.comments.length === 0) ? (
            <p className="text-sm text-center py-8" style={{ color: '#88929B' }}>
              Aucun commentaire pour le moment. Soyez le premier à réagir !
            </p>
          ) : (
            post.comments.map((comment) => (
              <div
                key={comment.id}
                className="p-4"
                style={{ backgroundColor: '#F9F9F8' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium" style={{ color: '#0A192F' }}>
                    <FiUser className="w-4 h-4" style={{ color: '#C59B6C' }} />
                    {comment.auteurNom}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: '#88929B' }}>
                      {formatDate(comment.dateCreation)}
                    </span>
                    {user && (user.id === comment.userId || user.role === 'admin') && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="p-1 transition-colors"
                        style={{ color: '#88929B' }}
                        onMouseEnter={(e) => e.target.style.color = '#FF6B6B'}
                        onMouseLeave={(e) => e.target.style.color = '#88929B'}
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap" style={{ color: '#3A4045' }}>
                  {comment.contenu}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
